import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  Fragment,
} from "react";
import ConfigService from "../../services/ConfigService";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Report2Header from "./Report2Header/Report2Header";
import SubstituteReportViewer from "./CustomizedReports/Substitute/SubstituteReportViewer";
import Spacer from "../../components/Spacer";
import ErrorBanner from "../../components/ErrorBanner";
import SearchBar from "../../components/SearchBar";
import TabBar from "../../components/TabBar";
import RoleCheckService from "../../services/RoleCheckService";
import CreateButton from "../../components/CreateButton";
import ReportTile from "../../components/ReportTile";
import Button from "../../components/Button";
import ModalBackdrop from "../../components/Modal";
import ReportService from "../../services/ReportService";
import { deepCopy } from "../../functions/utilities";
import ReportViewer from "./ReportViewer";
import CreateLabel from "../LabelEditor/CreateLabel";
import { ScreenContext } from "../../contexts/ScreenContext";
import { useMediaQuery } from "react-responsive";
import styles from "./Reports2.module.css";

const showableMobileReports = [
  "Replenishment",
  "Production",
  "Summary Bag Report",
];

const Reports = ({
  user,
  navToggle,
  returnURL,
  reportName,
  dataFromDashboard,
  dataFromDispensingOrders,
}) => {
  const { t } = useTranslation("reports");
  const { handleShowSmScreenHeader, smallScreen } = useContext(ScreenContext);
  const smScreen = useMediaQuery(smallScreen);

  const navigate = useNavigate();

  const actionListItems = [
    {
      type: "modify",
      description: t("action0"),
    },
    {
      type: "copy",
      description: t("action2"),
    },
    {
      type: "delete",
      description: t("action1"),
    },
    // {
    //   type: "standardize",
    //   description: "standardize",
    // },
  ];

  const tabs = [
    {
      index: 0,
      text: t("reportsTab"),
      icon: null,
    },
    {
      index: 1,
      text: t("labelsTab"),
      icon: null,
    },
  ];

  const handleSelectedTab = (index) => {
    if (index === 0) {
      setSelectedTab({ ...tabs[0] });
    } else if (index === 1) {
      setSelectedTab({ ...tabs[1] });
    }
    setSelectedTab(tabs[index]);
  };

  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [userAccessibleSites, setUserAccessibleSites] = useState([]);
  const [subscribeToReport, setSubscribeToReport] = useState(false);
  const emptyReportObj = {
    title: "",
    isEnterprise: false,
    isStandard: false,
    ignoreDateRange: false,
    collections: [],
    fields: [],
    functionFields: [],
    functionLookups: {},
    sites: [],
    groupedField: "No grouping",
    showCustomReport: false,
    customizedReport: "",
  };
  const [error, setError] = useState("");
  const [filterText, setFilterText] = useState("");
  const [currentAction, setCurrentAction] = useState(0);
  const [reportsList, setReportsList] = useState([]);
  const [customizedReportsInfo, setCustomizedReportsInfo] = useState(null);
  const [reportObj, setReportObj] = useState(emptyReportObj);
  const [reportToView, setReportToView] = useState(null);
  const [ImportedComponent, setImportedComponent] = useState(null);
  const [originalReportObj, setOriginalReportObj] = useState("");
  const [fromHeaderPage, setFromHeaderPage] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [updateReports, setUpdateReports] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [showDeleteReportWarning, setShowDeleteReportWarning] = useState(false);
  const [reportObjSelectedForDeletion, setReportObjSelectedForDeletion] =
    useState(null);
  const [showTabBar, setShowTabBar] = useState(true);

  let importCustomizedReport = useCallback(
    (reportName) => {
      const customReportIndex = customizedReportsInfo.findIndex(
        (customizedReportInfoObj) =>
          customizedReportInfoObj.name === reportName,
      );
      if (customReportIndex !== -1) {
        const foundCustomReport = customizedReportsInfo[customReportIndex];
        switch (foundCustomReport.name) {
          case "Refill Report":
            import(
              "./CustomizedReports/RefillReportForHealthDirect/RefillReport"
            ).then((component) => {
              setImportedComponent(component);
            });
            break;
          case "Refill Report - Routed Orders":
            import(
              "./CustomizedReports/RefillReportRoutedOrders/RefillReport"
            ).then((component) => {
              setImportedComponent(component);
            });
            break;
          case "Replenishment":
            import("./FillList/FillList").then((component) => {
              setImportedComponent(component);
            });
            break;
          case "Substitute":
            import(
              "./CustomizedReports/Substitute/SubstituteReportViewer"
            ).then((component) => {
              setImportedComponent(component);
            });
            break;
          case "Summary Bag Report":
            import(
              "./CustomizedReports/SummaryBagReport/SummaryBagReportViewer"
            ).then((component) => {
              setImportedComponent(component);
            });
            break;
          case "Tray Check Summary":
            import(
              "./CustomizedReports/TrayCheckSummary/TrayCheckSummaryReportViewer"
            ).then((component) => {
              setImportedComponent(component);
            });
            break;
          default:
            setImportedComponent(null);
        }
      } else {
        setImportedComponent(null);
      }
    },
    [customizedReportsInfo],
  );

  const TabBarComponent = (
    <TabBar
      tabs={tabs}
      onSelectedTab={handleSelectedTab}
      fontSize={"14px"}
      dynamicActiveIndex={selectedTab.index}
    />
  );

  useEffect(() => {
    // GET REPORTS FROM DATABASE
    if (!reportsList.length || updateReports)
      ReportService.list2(user)
        .then((results) => {
          setReportsList(results);
        })
        .catch((error) => {
          setError(error);
        });

    setUpdateReports(false);
  }, [user, updateReports]);

  useEffect(() => {
    // Gets information about all customized reports
    ConfigService.customizedReports(user).then((customizedReports) => {
      setCustomizedReportsInfo(customizedReports);
    });
  }, [user]);

  useEffect(() => {
    // set ImportedComponent for custom report view
    if (reportToView?.customizedReport) {
      importCustomizedReport(reportToView.customizedReport);
    }
  }, [reportToView, user, importCustomizedReport]);

  useEffect(() => {
    // set ImportedComponent for custom report preview
    if (isPreview && reportObj?.customizedReport) {
      importCustomizedReport(reportObj?.customizedReport);
    }
  }, [reportObj, isPreview, importCustomizedReport]);

  useEffect(() => {
    if (subscribeToReport) {
      handleCreate();
    }
  }, [subscribeToReport]);

  const handleSetCurrentAction = (actionNum) => {
    setCurrentAction(actionNum);
  };

  let ViewerComponent; // displayed when reportToView is true

  /****************************  set Viewer component *******************************/
  if (!smScreen && reportToView && ImportedComponent) {
    const ImportedViewerComponent = ImportedComponent.default;
    ViewerComponent = (
      <ImportedViewerComponent
        isPreview={false}
        reportObj={reportToView}
        user={user}
        onClose={() => {
          setReportObj(emptyReportObj);
          setReportToView(null);
          setImportedComponent(null);
        }}
        onHandleSetCurrentAction={handleSetCurrentAction}
      />
    );
  } else if (smScreen && reportToView && ImportedComponent) {
    const ImportedViewerComponent = ImportedComponent.default;
    ViewerComponent = (
      <ImportedViewerComponent
        isPreview={false}
        reportObj={reportToView}
        user={user}
        onClose={() => {
          setReportObj(emptyReportObj);
          setReportToView(null);
          setImportedComponent(null);
        }}
        onHandleSetCurrentAction={handleSetCurrentAction}
      />
    );
  } else if (reportToView?.title) {
    ViewerComponent = (
      <ReportViewer
        isPreview={false}
        reportObj={reportToView}
        user={user}
        onClose={() => {
          setReportObj(emptyReportObj);
          setReportToView(null);
          setImportedComponent(null);
        }}
        returnURL={returnURL}
      />
    );
  }
  /**********************************************************************************/

  useEffect(() => {
    // Get the pharmacy sites that this user has access rights to
    RoleCheckService.getAccessibleSites(user)
      .then((sites) => {
        sites.sort((a, b) => {
          if (a.name.toUpperCase() < b.name.toUpperCase()) {
            return -1;
          }
          if (b.name.toUpperCase() < a.name.toUpperCase()) {
            return 1;
          }
          return 0;
        });
        setUserAccessibleSites(sites);
      })
      .catch((error) => {
        console.log("error");
      });
  }, [user]);

  useEffect(() => {
    // ??????
    setReportObj(originalReportObj || emptyReportObj);
  }, [originalReportObj]);

  useEffect(() => {
    if (currentAction === 0) {
      handleReportObj(emptyReportObj);
      setOriginalReportObj(emptyReportObj);
    }
  }, [currentAction]);

  useEffect(() => {
    // Reset to main Report view when menu item is clicked
    setReportToView(null);
    setCurrentAction(0);
  }, [navToggle]);

  useEffect(() => {
    // Reset view to reports selection screen if mobile view and non-showable mobile report is active
    if (
      smScreen &&
      reportToView &&
      reportToView?.title &&
      !showableMobileReports.includes(reportToView.title)
    ) {
      setReportToView(null);
    }
  }, [smScreen, reportToView]);

  useEffect(() => {
    // If mobile view, show mobile header (navbar hamburger, alarm bell, user profile photo)
    smScreen && handleShowSmScreenHeader(true);
  }, [smScreen, handleShowSmScreenHeader]);

  const handleShowTabBar = (boolVal) => {
    setShowTabBar(boolVal);
  };

  const handleReportObj = (obj) => {
    setReportObj(obj);
  };

  const handleSearch = (searchText) => {
    setFilterText(searchText.toUpperCase());
  };

  const handleEditMode = (bool) => {
    setEditMode(bool);
  };

  const handleCreate = () => {
    setOriginalReportObj("");
    setCurrentAction(1);
    handleIsNew();
  };

  const handleCreated = () => {
    setUpdateReports(true);
    setCurrentAction(0);
    setSubscribeToReport(false);
  };

  const handleUpdated = () => {
    setUpdateReports(true);
    setCurrentAction(0);
    handleEditMode(false);
  };

  const getActionMenu = (reportCreatorId, customizedReport) => {
    let listItems = [...actionListItems];
    // Allow a report to be modified only if it is not a customized report and if the user is the same as the report owner.
    // Allow a report to be deleted only if the user is the same as the report owner.
    if (
      !RoleCheckService.editCustomReports(user) &&
      !RoleCheckService.removeCustomReports(user) &&
      reportCreatorId !== user._id
    ) {
      let index = listItems.findIndex((o) => o.type === "modify");
      listItems.splice(index, 1);
      index = listItems.findIndex((o) => o.type === "delete");
      listItems.splice(index, 1);
    } else if (customizedReport) {
      // let index = listItems.findIndex((o) => o.type === "modify");
      // listItems.splice(index, 1);
    }
    return listItems;
  };

  const getStandardActionMenu = (reportObj) => {
    let listItems = [];

    if (RoleCheckService.canCreateReports(user)) {
      listItems = [...actionListItems];
      // remove modify & delete
      let index = listItems.findIndex((o) => o.type === "modify");
      listItems.splice(index, 1);
      index = listItems.findIndex((o) => o.type === "delete");
      listItems.splice(index, 1);
    }
    return listItems;
  };

  const handleAction = async (action, reportObj) => {
    let index = -1;

    for (let i = 0; i < reportsList.length; i++) {
      if (reportObj === reportsList[i].reportObject) {
        index = i;
      }
    }

    const startingReportObj = fromHeaderPage
      ? deepCopy(reportsList[index].reportObject)
      : deepCopy(reportsList[index].reportObject);

    if (!startingReportObj.hasOwnProperty("functionLookups")) {
      startingReportObj.functionLookups = {};
    }

    switch (action.type) {
      case "modify":
        if (startingReportObj) {
          setEditMode(true);
          setIsNew(false);
          setCurrentAction(1);
          setOriginalReportObj(startingReportObj);
        }
        break;
      case "delete":
        await handleDeleteReportWarning(reportObj);
        break;
      case "copy":
        let reportObjToCopy = { ...reportObj };
        reportObjToCopy.title = `${reportObj.title}-copy`;
        reportObjToCopy.isStandard = false;
        await handleCopy(reportObjToCopy);
        break;
      // case "standardize":
      //   await handleStandardize(reportObj);
      //   break;
      default:
        break;
    }
  };

  const handleIsNew = () => {
    setIsNew(true);
  };

  const handleIsNotNew = () => {
    setIsNew(false);
  };

  const checkIfReportNameExists = (name) => {
    let nameFound = false;

    for (let i = 0; i < reportsList.length; i++) {
      if (
        reportsList[i].name === name &&
        reportsList[i].account === user.account._id &&
        !reportsList[i].type
      ) {
        nameFound = true;
        break;
      }
    }
    return nameFound;
  };

  const handleSaveData = async () => {
    if (isNew) {
      ReportService.create2(user, reportObj)
        .then((newReport) => {
          // console.log("newReport: ", newReport);
          handleCreated();
        })
        .catch((error) => {
          setError(error);
        });
    } else {
      let reportToUpdate = reportsList.find(
        (o) =>
          o.name === originalReportObj.title &&
          o.account === user.account._id &&
          !o.type,
      );

      let modifiedReportToUpdate = {
        ...reportToUpdate,
        reportObject: reportObj,
        name: reportObj.title,
      };

      ReportService.update(user, modifiedReportToUpdate)
        .then((updatedReport) => {
          handleUpdated();
        })
        .catch((error) => {
          setError(error);
        });
    }
  };

  const previewButton = (
    <Button
      labelName={t("previewButtonLabel")}
      minWidth={"123px"}
      isPrimary={true}
      onClick={() => setIsPreview(true)}
      isDisabled={
        reportObj.title &&
        (reportObj.collections.length > 0 || reportObj?.customizedReport) &&
        (reportObj.fields.length > 0 || reportObj?.customizedReport)
          ? false
          : true
      }
    />
  );

  const handleCopy = async (reportObjToCopy) => {
    await ReportService.create2(user, reportObjToCopy)
      .then((newReport) => {
        setUpdateReports(true);
      })
      .catch((error) => {
        setError(error);
      });
  };

  const handleDeleteReportWarning = async (reportObj) => {
    setReportObjSelectedForDeletion(reportObj);
    setShowDeleteReportWarning(true);
  };

  const handleCancelDeleteReport = () => {
    setShowDeleteReportWarning(false);
    setReportObjSelectedForDeletion(null);
  };

  const handleDeleteReport = async () => {
    if (reportObjSelectedForDeletion) {
      let reportToDelete = reportsList.find(
        (o) => o.name === reportObjSelectedForDeletion.title,
      );

      await ReportService.delete(user, reportToDelete)
        .then((reportDoc) => {
          setUpdateReports(true);
        })
        .catch((error) => {
          setError(error);
        })
        .finally(() => {
          setShowDeleteReportWarning(false);
          setReportObjSelectedForDeletion(null);
        });
    }
  };

  const handleFromHeaderPage = () => {
    setFromHeaderPage(true);
    setSubscribeToReport(false);
  };

  const handleNotFromHeaderPage = () => {
    setFromHeaderPage(false);
  };

  let ReportViewerForPreview;

  /****************************** handle preview view ******************************/
  if (isPreview && reportObj && reportObj?.customizedReport) {
    if (ImportedComponent?.default) {
      const ImportedViewerComponent = ImportedComponent.default;

      ReportViewerForPreview = (
        <ImportedViewerComponent
          isPreview={true}
          reportObj={reportObj}
          user={user}
          onClose={() => {
            setIsPreview(false);
          }}
        />
      );
    }
  } else if (isPreview) {
    ReportViewerForPreview = (
      <ReportViewer
        isPreview={true}
        reportObj={reportObj}
        user={user}
        onClose={() => {
          setIsPreview(false);
        }}
        returnURL={returnURL}
      />
    );
  }

  let neededReportObj;
  if (
    dataFromDashboard &&
    dataFromDashboard?.returnURL &&
    dataFromDashboard?.reportName
  ) {
    neededReportObj = reportsList.find(
      (element) => element?.name === dataFromDashboard.reportName,
    );
  } else if (
    dataFromDispensingOrders &&
    dataFromDispensingOrders?.returnURL &&
    dataFromDispensingOrders?.customizedReport &&
    dataFromDispensingOrders.customizedReport === "Substitute"
  ) {
    neededReportObj = reportsList.find(
      (element) => element?.reportObject?.customizedReport === "Substitute",
    );
  }

  // Render
  if (dataFromDashboard && neededReportObj && neededReportObj?.reportObject) {
    return (
      <ReportViewer
        isPreview={false}
        reportObj={neededReportObj.reportObject}
        user={user}
        onClose={() => {
          setReportObj(emptyReportObj);
          setReportToView(null);
          setImportedComponent(null);
          navigate("/dashboard", {
            state: {
              dashboardReturnState: {
                returnView: dataFromDashboard?.returnView,
                selectedSite: dataFromDashboard?.selectedSite,
              },
            },
          });
        }}
        returnURL={returnURL}
      />
    );
  } else if (
    dataFromDispensingOrders &&
    neededReportObj &&
    neededReportObj?.reportObject
  ) {
    return (
      <SubstituteReportViewer
        isPreview={false}
        reportObj={neededReportObj.reportObject}
        dispensingOrderIdFromOrder={dataFromDispensingOrders?.dispensingOrderId}
        dispensingOrderPackagedOn={dataFromDispensingOrders?.packagedOn}
        user={user}
        onClose={() => {
          setReportObj(emptyReportObj);
          setReportToView(null);
          setImportedComponent(null);
          navigate("/dispensing-orders", {
            state: {
              dispensingOrdersReturnState: {
                selectedSiteIndex: dataFromDispensingOrders?.selectedSiteIndex,
              },
            },
          });
        }}
        returnURL={returnURL}
      />
    );
  } else if (reportToView) {
    // handles report view
    return ViewerComponent;
  } else if (currentAction === 1) {
    // handles report preview
    return (
      <Report2Header
        reportObj={reportObj}
        subscribeToReport={subscribeToReport}
        handleReportObj={handleReportObj}
        originalReportObj={originalReportObj}
        user={user}
        onClose={() => {
          setCurrentAction(0);
        }}
        onSaveData={handleSaveData}
        handleFromHeaderPage={handleFromHeaderPage}
        handleNotFromHeaderPage={handleNotFromHeaderPage}
        onIsNotNew={handleIsNotNew}
        editMode={editMode}
        onEditMode={handleEditMode}
        checkIfReportNameExists={checkIfReportNameExists}
        previewButton={previewButton}
        ReportViewerForPreview={ReportViewerForPreview}
        isPreview={isPreview}
        customizedReportsInfo={customizedReportsInfo}
      />
    );
  } else if (smScreen || selectedTab.index === 0) {
    const handleRunReport = (reportObjFromDataBase) => {
      setReportToView(reportObjFromDataBase.reportObject);
    };
    return (
      <Fragment>
        {showDeleteReportWarning && (
          <ModalBackdrop
            width="100%"
            height="100%"
            top="0"
            left="0"
            padding="0"
            showButton={false}
            backgroundColor="#98a9bc4d"
            borderRadius="0"
          >
            <div className={styles.Reports__deleteWarningModal}>
              <p>{t("deleteMessage")}</p>
              <div className={styles.Reports__ModalButtonsGroup}>
                <Button
                  labelName={t("cancelButtonLabel")}
                  minWidth={"123px"}
                  isPrimary={false}
                  onClick={handleCancelDeleteReport}
                />
                <Spacer space={20} unit={"px"} />
                <Button
                  labelName={t("deleteButtonLabel")}
                  minWidth={"123px"}
                  isPrimary={true}
                  onClick={handleDeleteReport}
                />
              </div>
            </div>
          </ModalBackdrop>
        )}
        <div className={styles.Reports__view}>
          {!smScreen && (
            <div className={styles.Reports__pageHeaderContainer}>
              {TabBarComponent}
            </div>
          )}

          {smScreen && (
            <div className={styles.Reports__SearchBarContainer}>
              <SearchBar
                placeholder={t("searchPlaceholderMobile")}
                onSearch={handleSearch}
                focus={true}
              />
            </div>
          )}
          {error && (
            <>
              <Spacer space={70} unit={"px"} />
              <ErrorBanner
                message={error}
                onClose={() => {
                  setError(null);
                }}
              />
              <Spacer space={10} unit={"px"} />
            </>
          )}
          {!smScreen && (
            <div className={styles.Reports__header}>
              <Spacer space={26} unit={"px"} />
              <div
                className={styles.Reports__row}
                style={{ justifyContent: "space-between" }}
              >
                <SearchBar
                  placeholder={t("searchPlaceholder")}
                  onSearch={handleSearch}
                  focus={true}
                />
                <div className={styles.Reports__reportOptions}>
                  {RoleCheckService.canCreateReports(user) && (
                    <CreateButton
                      onClick={() => setSubscribeToReport(true)}
                      isEnabled={true}
                      labelName={t("subscribeButtonLabel")}
                    />
                  )}
                  {RoleCheckService.canCreateReports(user) && (
                    <CreateButton
                      onClick={handleCreate}
                      isEnabled={true}
                      labelName={t("createReportButtonLabel")}
                    />
                  )}
                </div>
              </div>
              <Spacer space={50} unit={"px"} />
            </div>
          )}

          <main className={styles.Reports__mainBody}>
            <div className={styles.Reports__row}>
              <div className={styles.Reports__title}>{t("templateTitle")}</div>
            </div>
            <Spacer space={smScreen ? 16 : 30} unit={"px"} />

            {/**************************** List Standard Reports ****************************/}
            <div
              className={`${styles.Reports__container} ${styles["Reports__container--standard"]}`}
            >
              {reportsList
                .filter((r) => {
                  return (
                    !r.type &&
                    r?.reportObject?.isStandard &&
                    (!smScreen || showableMobileReports.includes(r.name)) &&
                    (filterText
                      ? r.name.toUpperCase().includes(filterText)
                      : true)
                  );
                })
                .map((reportObjFromDatabase) => {
                  return (
                    <ReportTile
                      key={reportObjFromDatabase._id}
                      imageName={"barChart"}
                      imageFillColor={"#089BAB"}
                      title={t(reportObjFromDatabase.reportObject.title, {
                        ns: "customReports",
                      })}
                      isTemplate={false}
                      actions={getStandardActionMenu(
                        reportObjFromDatabase.reportObject,
                      )}
                      onAction={(action) => {
                        handleAction(
                          action,
                          reportObjFromDatabase.reportObject,
                        );
                      }}
                      onRunReport={() => {
                        setReportToView(reportObjFromDatabase.reportObject);
                      }}
                    />
                  );
                })}
            </div>
            {/********************************************************************************/}

            {/****************************** List Custom Reports *****************************/}

            {!smScreen && (
              <>
                <Spacer space={30} unit={"px"} />
                <div className={styles.Reports__row}>
                  <div className={styles.Reports__title}>
                    {t("customReportTitle")}
                  </div>
                </div>
                <Spacer space={30} unit={"px"} />
                <div
                  className={`${styles.Reports__container} ${styles["Reports__container--custom"]}`}
                >
                  <ReportTile
                    imageName={"barChart"}
                    imageFillColor={"#A7BBCF"}
                    title={t("createReportButtonLabel")}
                    isTemplate={true}
                    onCreateTemplate={handleCreate}
                  />
                  {reportsList
                    .filter((r) => {
                      let showCustomReport = false;
                      let matchedUserSites = [];
                      if (!r?.reportObject?.isEnterprise) {
                        matchedUserSites = r.reportObject?.sites.filter(
                          (rValue) => {
                            return userAccessibleSites.filter((sitesValue) => {
                              return sitesValue._id === rValue._id;
                            });
                          },
                        );
                      }
                      if (
                        r?.user === user._id ||
                        (r?.reportObject?.showCustomReport &&
                          r?.reportObject?.isEnterprise &&
                          r?.account === user.account._id)
                      ) {
                        showCustomReport = true;
                      } else if (
                        r?.reportObject?.showCustomReport &&
                        r?.account === user.account._id &&
                        matchedUserSites.length
                      ) {
                        showCustomReport = true;
                      }
                      return (
                        !r.type &&
                        r.account === user.account._id &&
                        !r?.reportObject?.isStandard &&
                        (filterText
                          ? r.name.toUpperCase().includes(filterText) &&
                            showCustomReport
                          : showCustomReport)
                      );
                    })
                    .map((reportObjFromDatabase, index) => {
                      return (
                        <ReportTile
                          key={index}
                          imageName={"barChart"}
                          imageFillColor={"#089BAB"}
                          title={t(reportObjFromDatabase.reportObject.title, {
                            ns: "customReports",
                          })}
                          isTemplate={false}
                          actions={getActionMenu(
                            reportObjFromDatabase.user,
                            reportObjFromDatabase.reportObject
                              ?.customizedReport,
                          )}
                          onAction={(action) => {
                            handleAction(
                              action,
                              reportObjFromDatabase.reportObject,
                            );
                          }}
                          onRunReport={() =>
                            handleRunReport(reportObjFromDatabase)
                          }
                        />
                      );
                    })}
                </div>
              </>
            )}
            {/********************************************************************************/}
          </main>
        </div>
      </Fragment>
    );
  } else {
    return (
      <div className={styles.Reports__labelEditorPageContainer}>
        {showTabBar && TabBarComponent}
        {
          <CreateLabel
            user={user}
            onSelectedTab={handleSelectedTab}
            onShowTabBar={handleShowTabBar}
          />
        }
      </div>
    );
  }
};

export default Reports;
